"use client";

import { ReactNode, useCallback, useEffect, useState } from "react";
import { subscriptionEvents } from "../utils/api-client";
import { PlanLimitDialog, PlanLimitDialogProps } from "./PlanLimitDialog";
import { useToast } from "./ToastNotification";

const RESOURCE_MAP: Record<string, PlanLimitDialogProps["resource"]> = {
  vehicles: "vehicle",
  vehicle: "vehicle",
  bookings: "booking",
  booking: "booking",
  customers: "customer",
  customer: "customer",
};

export function SubscriptionGuardProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();

  const [limitDialog, setLimitDialog] = useState<{
    open: boolean;
    resource: PlanLimitDialogProps["resource"];
    limit?: number;
    used?: number;
  }>({ open: false, resource: "vehicle" });

  const handleLimitReached = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail as {
      error?: string;
      code?: string;
      resource?: string;
      limit?: number;
      used?: number;
    };

    const resource = RESOURCE_MAP[detail.resource ?? ""] ?? "vehicle";

    setLimitDialog({
      open: true,
      resource,
      limit: detail.limit,
      used: detail.used,
    });
  }, []);

  const handleModuleBlocked = useCallback(
    (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        error?: string;
        code?: string;
        module?: string;
      };
      void detail;
      showToast("This feature is not available on your current plan.", "error");
    },
    [showToast],
  );

  useEffect(() => {
    subscriptionEvents.addEventListener("limit-reached", handleLimitReached);
    subscriptionEvents.addEventListener("module-blocked", handleModuleBlocked);

    return () => {
      subscriptionEvents.removeEventListener("limit-reached", handleLimitReached);
      subscriptionEvents.removeEventListener("module-blocked", handleModuleBlocked);
    };
  }, [handleLimitReached, handleModuleBlocked]);

  const closeDialog = useCallback(() => {
    setLimitDialog((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      {children}
      <PlanLimitDialog
        open={limitDialog.open}
        onClose={closeDialog}
        resource={limitDialog.resource}
        limit={limitDialog.limit}
        used={limitDialog.used}
      />
    </>
  );
}
